import { put, delay, takeLatest, select } from "@redux-saga/core/effects";
import { createViolation, createViolationComment, fetchAllUnits, fetchAllViolations, fetchViolationById, fetchViolationComments, updateStatusViolation, updateViolation } from "../../../Utils/BackendAPICalls";
import { Actions, Types } from "../../Reducer/rootReducer";
import { fetchUnit } from "../../selectors";
import { Comment, Violation, ViolationStatus } from "../../../Types/UnitFeatureTypes";

interface FetchUnitAction {
    payload: {
        id?: string;
        fresh?: boolean;
    };
}

interface ViolationAction {
    payload: {
        violationData: Violation;
    };
}

interface FetchViolationAction {
    payload: {
        id?: string;
        offset?: number;
        sortColumn?: string;
        sortDirection?: string;
    };
}

interface UpdateStatusViolationAction {
    payload: {
        id: number;
        status: ViolationStatus;
    };
}

interface ViolationComment {
    payload: {
        comment: Comment;
    };
}

export function* fetchViolationRequestFunction(action: FetchViolationAction): Generator<any, void, Response> {
    const { id, offset, sortColumn, sortDirection } = action.payload;
    let response: any = null;
    try {
        yield delay(100);
        if (id) {
            response = yield fetchViolationById(id);
        } else {
            response = yield fetchAllViolations(offset ?? 0, sortColumn ?? 'created_on', sortDirection ?? 'desc');
        }
        if (!response || response?.error) {
            yield put(Actions.fetchViolation.violationActions.failure({ message: response.error }));
        } else {
            yield put(Actions.fetchViolation.violationActions.success({ violations: response.violations, violation: response.violation, count: response.count }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchViolation.violationActions.failure(error));
    }
}

export function* createViolationRequestFunction(action: ViolationAction): Generator<any, void, Response> {
    const { violationData } = action.payload;
    if (!violationData) return;
    try {
        yield delay(100);
        const response: any = yield createViolation(violationData);
        if (!response || response?.error) {
            yield put(Actions.violation.createViolationRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.violation.createViolationRequestAction.success({ id: response.id }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.violation.createViolationRequestAction.failure(error));
    }
}

export function* updateViolationRequestFunction(action: ViolationAction): Generator<any, void, Response> {
    const { violationData } = action.payload;
    if (!violationData) return;
    try {
        yield delay(100);
        const response: any = yield updateViolation(violationData);
        if (!response || response?.error) {
            yield put(Actions.violation.updateViolationRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.violation.updateViolationRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.violation.createViolationRequestAction.failure(error));
    }
}

export function* updateStatusViolationRequestFunction(action: UpdateStatusViolationAction): Generator<any, void, Response> {
    const { id, status } = action.payload;
    if (!id) return;
    try {
        yield delay(100);
        const response: any = yield updateStatusViolation(id, status);
        if (!response || response?.error) {
            yield put(Actions.violation.updateStatusViolationRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.violation.updateStatusViolationRequestAction.success());
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.violation.updateStatusViolationRequestAction.failure(error));
    }
}

export function* fetchViolationCommentsRequestFunction(action: FetchViolationAction): Generator<any, void, Response> {
    const { id } = action.payload;
    if (!id) return;
    try {
        yield delay(100);
        const response: any = yield fetchViolationComments(id);
        if (!response || response?.error) {
            yield put(Actions.fetchViolation.violationCommentActions.failure({ message: response.error }));
        } else {
            yield put(Actions.fetchViolation.violationCommentActions.success({ comments: response.comments }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.fetchViolation.violationCommentActions.failure(error));
    }
}

export function* createViolationCommentRequestFunction(action: ViolationComment): Generator<any, void, Response> {
    const { comment } = action.payload;
    if (!comment) return;
    try {
        yield delay(100);
        const response: any = yield createViolationComment(comment);
        if (!response || response?.error) {
            yield put(Actions.violation.createViolationCommentRequestAction.failure({ message: response.error }));
        } else {
            yield put(Actions.violation.createViolationCommentRequestAction.success());
            yield put(Actions.fetchViolation.violationCommentActions.request({
                id: comment.resource_id
            }));
        }
    } catch (err) {
        const error = { message: "fatalFailure" };
        yield put(Actions.violation.createViolationCommentRequestAction.failure(error));
    }
}

export default function* violationSaga() {
    yield takeLatest(Types.FETCH_VIOLATION_REQUEST, fetchViolationRequestFunction);
    yield takeLatest(Types.CREATE_VIOLATION_REQUEST, createViolationRequestFunction);
    yield takeLatest(Types.UPDATE_VIOLATION_REQUEST, updateViolationRequestFunction)
    yield takeLatest(Types.UPDATE_STATUS_VIOLATION_REQUEST, updateStatusViolationRequestFunction)
    yield takeLatest(Types.FETCH_VIOLATION_COMMENT_REQUEST, fetchViolationCommentsRequestFunction)
    yield takeLatest(Types.CREATE_VIOLATION_COMMENT_REQUEST, createViolationCommentRequestFunction);
}
